import { useMemo } from 'react';

import { SurveyName } from '~/lib/constants/survey/SurveyName';
import { SurveyResponseStatus } from '~/lib/constants/survey/SurveyResponseStatus';
import { GroupedAssessment } from '~/lib/types/survey/GroupedAssessment';
import { calculateDaysLeftIn30DayWindowFromAdmitDate } from '~/lib/util/assessments.util';
import { hasBeenXOrMoreDaysSinceDate } from '~/lib/util/date.util';
import { useGetGroupedAssessmentsQuery } from '~/store/services/assessments';
import { useGetPatientTreatmentEpisodesQuery } from '~/store/services/clinicalData';

const surveyPriority: Record<string, number> = {
  [SurveyName.EDE_Q]: 1,
  [SurveyName.PARDI_ARQ_SELF]: 1,
  [SurveyName.PHQ9]: 2,
  [SurveyName.PHQ8]: 3,
  [SurveyName.GAD7]: 4,
  [SurveyName.QUALITY_OF_LIFE]: 5,
};

const overdueEligibleStatuses = new Set<string>([
  SurveyResponseStatus.NEW,
  SurveyResponseStatus.IN_PROGRESS,
  SurveyResponseStatus.REQUESTED,
]);

const daysBeforeOverdue = 10;

interface UseOverdueCoreAssessmentsResult {
  admitDate: string;
  isError: boolean;
  isLoading: boolean;
  overdueCoreAssessments: GroupedAssessment[];
}

export const useOverdueCoreAssessments = (
  patientId: string,
): UseOverdueCoreAssessmentsResult => {
  const { data: treatmentEpisodesData } =
    useGetPatientTreatmentEpisodesQuery(patientId);
  const mostRecentCreatedEpisode = treatmentEpisodesData?.data
    ? [...treatmentEpisodesData.data].sort((a, b) =>
        b.createdAt.localeCompare(a.createdAt),
      )[0]
    : null;
  const admitDate = mostRecentCreatedEpisode?.admitDate;

  const {
    data: groupedAssessments,
    isError,
    isLoading,
  } = useGetGroupedAssessmentsQuery({
    patientId,
  });

  const coreSurveys = useMemo(() => {
    if (!groupedAssessments?.surveys) return [];

    return groupedAssessments.surveys
      .filter((survey) => survey.isCore && survey.assessments?.length)
      .sort(
        (a, b) =>
          (surveyPriority[a.name] ?? Number.MAX_SAFE_INTEGER) -
          (surveyPriority[b.name] ?? Number.MAX_SAFE_INTEGER),
      );
  }, [groupedAssessments]);

  const reorderedSurveys = useMemo(
    () =>
      coreSurveys.map((survey) => ({
        ...survey,
        assessments: [...survey.assessments].sort((a, b) =>
          b.createdDate.localeCompare(a.createdDate),
        ),
      })),
    [coreSurveys],
  );

  const overdueCoreAssessments = useMemo(
    () =>
      reorderedSurveys.filter((survey) => {
        const firstAssessment = survey.assessments[0];
        const isOverdue =
          overdueEligibleStatuses.has(firstAssessment.status) &&
          hasBeenXOrMoreDaysSinceDate(
            daysBeforeOverdue,
            firstAssessment.createdDate,
          ) &&
          !firstAssessment.endDate;
        if (!isOverdue) return false;
        return (
          calculateDaysLeftIn30DayWindowFromAdmitDate(
            firstAssessment.createdDate,
            admitDate,
          ) > 0
        );
      }),
    // Same deps as the V1 inline memo this replaced, which recomputes on survey changes only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [reorderedSurveys],
  );

  return { admitDate, isError, isLoading, overdueCoreAssessments };
};
